import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Package, Search, Check } from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import { code128Geometry } from '../../utils/code128';
import { hardwareApi, money, type DeviceModelRow, type DeviceUnitRow } from '../../services/hardwareApi';
import { unitBadgeLabel, worstBadgeSeverity } from './unitBadgeSafe';
import { grnLabel } from './InventoryHubPage';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * BROWSE WHAT IS ON THE SHELF, ONE PAGE AT A TIME.
 *
 * Allocation used to load every in-stock unit in the estate on mount
 * (`listUnits({ state: 'in_stock', limit: 500 })`) and render up to forty per
 * model inline. Two costs: the panel got slower with every device the business
 * ever received, and the rows carried a date and a raw `grn_<uuid>` — nothing
 * that identifies the box in your hand.
 *
 * This fetches ONE model's stock, ten at a time, only once opened. The row
 * carries what receiving actually recorded, and the detail pane carries the
 * rest, because deciding whether THIS unit should go to a paying merchant is a
 * question about its condition, not its id.
 */

const PAGE_SIZE = 10;

const Barcode: React.FC<{ value: string; width: number; height: number }> = ({ value, width, height }) => {
  const { bars, modules } = useMemo(() => code128Geometry(value), [value]);
  if (!modules) return null;
  return (
    <svg viewBox={`0 0 ${modules} ${height}`} width={width} height={height}
      preserveAspectRatio="none" role="img" aria-label={`Barcode ${value}`} style={{ display: 'block' }}>
      {bars.map((b, i) => <rect key={i} x={b.x} y={0} width={b.width} height={height} fill="#1C1917" />)}
    </svg>
  );
};

const BADGE_CLASS: Record<string, string> = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  watch: 'border-amber-200 bg-amber-50 text-amber-700',
  fault: 'border-red-200 bg-[#FFF1F3] text-[#9F1239]',
};

const BadgePills: React.FC<{ ids?: string[] }> = ({ ids }) => (
  <>
    {(ids || []).map((b) => (
      <span key={b} className={`rounded-full border px-1.5 py-px text-[9px] font-bold uppercase tracking-wide ${
        BADGE_CLASS[worstBadgeSeverity([b]) || 'good']
      }`}>{unitBadgeLabel(b)}</span>
    ))}
  </>
);

/**
 * Known-faulty units sort LAST.
 *
 * The list is otherwise oldest-first, matching "allocate oldest in stock" —
 * FIFO is right for stock and wrong for a device with a screen fault, which
 * FIFO would hand to a paying merchant first. They stay visible and selectable:
 * hiding them would make "6 in stock" disagree with what an operator can see,
 * and sometimes a marked unit is deliberately the right one to send.
 */
export const sortForPicking = (units: DeviceUnitRow[]): DeviceUnitRow[] =>
  [...units].sort((a, b) => {
    const af = worstBadgeSeverity(a.badges || []) === 'fault' ? 1 : 0;
    const bf = worstBadgeSeverity(b.badges || []) === 'fault' ? 1 : 0;
    if (af !== bf) return af - bf;
    return (a.createdAt || 0) - (b.createdAt || 0);
  });

const Fact: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({ label, children, mono }) => (
  <div className="min-w-0">
    <div className="text-[9px] font-bold uppercase tracking-wide text-stone-400">{label}</div>
    <div className={`truncate text-[12px] text-stone-700 ${mono ? 'font-mono text-[11px]' : ''}`}>{children}</div>
  </div>
);

const StockPickerModal: React.FC<{
  model?: DeviceModelRow;
  modelId: string;
  /** How many this order still needs — the cap on selection. */
  outstanding: number;
  initialSelected: string[];
  vendorNameOf?: (vendorId?: string) => string | undefined;
  onClose: () => void;
  /** The chosen UNITS, not just ids: the panel renders each one's barcode. */
  onApply: (units: DeviceUnitRow[]) => void;
}> = ({ model, modelId, outstanding, initialSelected, vendorNameOf, onClose, onApply }) => {
  const [all, setAll] = useState<DeviceUnitRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [sel, setSel] = useState<Set<string>>(new Set(initialSelected));
  const [focusId, setFocusId] = useState<string | null>(null);
  const dataVersion = useDataVersion();
  const loadedOnce = useRef(false);

  // ONE model's stock, fetched when the modal opens — not every unit in the
  // estate on the parent's mount.
  const load = useCallback(async () => {
    // Spinner on the first read only — a re-read swaps the list in place.
    if (!loadedOnce.current) setLoading(true);
    setError(null);
    try {
      const res = await hardwareApi.listUnits({ state: 'in_stock', modelId, limit: 500 });
      setAll(sortForPicking(res.items || []));
      loadedOnce.current = true;
    } catch (e: any) {
      setError(e?.message || 'Could not read stock');
    } finally {
      setLoading(false);
    }
  }, [modelId, dataVersion]); // dataVersion: re-read after any save on the page
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return all;
    return all.filter((u) =>
      (u.serialNumber || '').toLowerCase().includes(s)
      || (u.trackingId || '').toLowerCase().includes(s)
      || grnLabel({ grnId: u.grnId || '', grnNumber: (u as any).grnNumber }).toLowerCase().includes(s));
  }, [all, q]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const rows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const focused = all.find((u) => u.unitId === focusId);

  const toggle = (unitId: string) => {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else if (next.size >= outstanding) return prev;
      else next.add(unitId);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-center gap-3 bg-black px-4 py-3 text-white">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-stone-400">Browse stock</div>
            <h4 className="truncate text-[15px] font-bold">{model?.modelName || modelId}</h4>
          </div>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="h-[3px] bg-gradient-to-r from-[#DC143C] via-[#9F1239] to-transparent" />

        <div className="flex items-center gap-2 border-b border-stone-200 bg-stone-50 px-4 py-2.5">
          <Search size={14} className="text-stone-400" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Filter by serial, RTA id or GRN…"
            aria-label="Filter stock"
            className="min-h-[36px] flex-1 rounded-lg border border-stone-300 px-2.5 text-[12.5px] outline-none focus:border-[#DC143C]"
          />
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[1fr_320px]">
          <div className="overflow-y-auto bg-stone-50 p-3">
            {loading ? (
              <RubyLoader variant="section" />
            ) : error ? (
              <p className="py-10 text-center text-[13px] text-[#DC143C]">{error}</p>
            ) : rows.length === 0 ? (
              <p className="py-10 text-center text-[13px] text-stone-500">
                {q ? 'No units match that.' : 'Nothing of this model is in stock.'}
              </p>
            ) : rows.map((u) => {
              const on = sel.has(u.unitId);
              const label = u.trackingId || u.unitId;
              return (
                <button
                  key={u.unitId}
                  type="button"
                  onClick={(e) => {
                    // Clicking the row LOOKS; the checkbox commits. Inspecting a
                    // device to check its condition should never be the same
                    // gesture as putting it on a customer's order.
                    if ((e.target as HTMLElement).closest('[data-box]')) toggle(u.unitId);
                    setFocusId(u.unitId);
                  }}
                  className={`mb-1.5 grid w-full grid-cols-[20px_44px_1fr_auto] items-center gap-3 rounded-lg border bg-white px-2.5 py-2 text-left ${
                    on ? 'border-[#DC143C] bg-[#FFF1F3]' : 'border-stone-200 hover:border-stone-400'
                  } ${focusId === u.unitId ? 'ring-2 ring-[#9F1239]' : ''}`}
                >
                  <span data-box className={`flex h-[17px] w-[17px] items-center justify-center rounded border-[1.5px] ${
                    on ? 'border-[#DC143C] bg-[#DC143C] text-white' : 'border-stone-400'
                  }`}>{on && <Check size={11} />}</span>
                  <ShimmerImage
                    src={model?.media?.heroImageUrl} alt={model?.modelName || 'Device'}
                    width={44} height={44} objectFit="contain" variant="light"
                    className="rounded-lg border border-stone-200 bg-stone-50"
                    fallbackBgColor="bg-stone-50"
                    fallbackIcon={<Package size={18} className="text-stone-300" />}
                  />
                  <span className="min-w-0">
                    <span className="block truncate font-mono text-[12.5px] font-semibold text-stone-900">
                      {u.serialNumber || label}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[10.5px] text-stone-500">
                      <Barcode value={label} width={110} height={17} />
                      <span className="font-mono">{label}</span>
                      {u.createdAt && <span>{new Date(u.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                      {u.grnId && <span className="font-mono">{grnLabel({ grnId: u.grnId, grnNumber: (u as any).grnNumber })}</span>}
                      <BadgePills ids={u.badges} />
                    </span>
                    {(u.notes || '').trim() && (
                      <span className="mt-0.5 block truncate text-[10.5px] italic text-stone-500">“{u.notes}”</span>
                    )}
                  </span>
                  <span className="text-right font-mono text-[12px] font-semibold">
                    {money(u.costCents ?? 0)}
                    <small className="mt-0.5 block font-sans text-[10px] font-normal text-stone-500">landed</small>
                  </span>
                </button>
              );
            })}
          </div>

          <aside className="hidden overflow-y-auto border-l border-stone-200 bg-white p-3.5 md:block">
            {!focused ? (
              <p className="px-2 py-16 text-center text-[12.5px] leading-relaxed text-stone-400">
                Select a device to see its full record — condition, note, cost and where it came from.
              </p>
            ) : (
              <>
                <ShimmerImage
                  src={model?.media?.heroImageUrl} alt={model?.modelName || 'Device'}
                  width="100%" height={110} objectFit="contain" variant="light"
                  className="mb-3 rounded-lg border border-stone-200 bg-stone-50"
                  fallbackBgColor="bg-stone-50"
                  fallbackIcon={<Package size={30} className="text-stone-300" />}
                />
                <div className="font-mono text-[15px] font-bold">{focused.serialNumber || focused.trackingId}</div>
                <div className="mt-0.5 font-mono text-[11px] text-stone-500">
                  {focused.trackingId} · {model?.modelName || modelId}
                </div>

                <div className="my-2.5 rounded-lg border border-stone-200 p-2 text-center">
                  <Barcode value={focused.trackingId || focused.unitId} width={280} height={46} />
                  <div className="mt-1 font-mono text-[10px] tracking-[0.13em] text-stone-700">
                    {focused.trackingId || focused.unitId}
                  </div>
                </div>

                <div className="mb-1.5 mt-3 text-[9.5px] font-bold uppercase tracking-wider text-stone-400">Condition</div>
                <div className="flex flex-wrap gap-1">
                  {(focused.badges || []).length
                    ? <BadgePills ids={focused.badges} />
                    : <span className="text-[11.5px] text-stone-400">No badges recorded.</span>}
                </div>

                {(focused.notes || '').trim() && (
                  <>
                    <div className="mb-1.5 mt-3 text-[9.5px] font-bold uppercase tracking-wider text-stone-400">Note</div>
                    <p className="rounded-lg border border-stone-200 bg-stone-50 p-2 text-[12px] leading-relaxed text-stone-700">
                      {focused.notes}
                    </p>
                  </>
                )}

                <div className="mb-1.5 mt-3 text-[9.5px] font-bold uppercase tracking-wider text-stone-400">Provenance</div>
                <div className="grid grid-cols-2 gap-2.5">
                  <Fact label="Received">
                    {focused.createdAt ? new Date(focused.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </Fact>
                  <Fact label="GRN" mono>
                    {focused.grnId ? grnLabel({ grnId: focused.grnId, grnNumber: (focused as any).grnNumber }) : '—'}
                  </Fact>
                  <Fact label="Vendor">{vendorNameOf?.(focused.vendorId) || focused.vendorId || '—'}</Fact>
                  <Fact label="State">{focused.state}</Fact>
                </div>

                <div className="mb-1.5 mt-3 text-[9.5px] font-bold uppercase tracking-wider text-stone-400">Landed cost</div>
                <div className="rounded-lg border border-stone-200 p-2">
                  <div className="text-right font-mono text-[16px] font-bold">{money(focused.costCents ?? 0)}</div>
                  {focused.costBreakdown && (
                    <dl className="mt-1.5 space-y-0.5 border-t border-stone-100 pt-1.5">
                      {([['Unit cost', 'unitCostCents'], ['Inbound shipping', 'inboundShippingCents'], ['Duty / customs', 'dutyCents']] as const).map(([lbl, k]) => (
                        <div key={k} className="flex justify-between text-[10.5px] text-stone-500">
                          <span>{lbl}</span>
                          <span className="font-mono text-stone-700">{money((focused.costBreakdown as any)[k] ?? 0)}</span>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => toggle(focused.unitId)}
                  className={`mt-3 min-h-[42px] w-full rounded-lg text-[12.5px] font-bold ${
                    sel.has(focused.unitId)
                      ? 'border border-stone-300 bg-white text-stone-700'
                      : 'border-0 text-white'
                  }`}
                  style={sel.has(focused.unitId) ? undefined : { background: '#DC143C' }}
                >
                  {sel.has(focused.unitId) ? 'Remove from selection' : 'Use this device'}
                </button>
              </>
            )}
          </aside>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-stone-200 px-4 py-2.5">
          <span className="text-[11.5px] text-stone-500">
            {filtered.length} in stock · page {safePage + 1} of {pages}
          </span>
          <div className="flex gap-1.5">
            <button disabled={safePage === 0} onClick={() => setPage(safePage - 1)}
              className="min-h-[36px] rounded-lg border border-stone-300 px-3 text-[12px] font-semibold text-stone-700 disabled:opacity-35">← Newer</button>
            <button disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)}
              className="min-h-[36px] rounded-lg border border-stone-300 px-3 text-[12px] font-semibold text-stone-700 disabled:opacity-35">Older →</button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-stone-200 px-4 py-2.5">
          <span className="text-[11.5px] text-stone-500">
            {sel.size ? `${sel.size} of ${outstanding} selected` : `Nothing selected — this order needs ${outstanding}`}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="min-h-[42px] rounded-lg border border-stone-300 px-4 text-[12.5px] font-semibold text-stone-700">Cancel</button>
            <button onClick={() => onApply(all.filter((u) => sel.has(u.unitId)))}
              className="min-h-[42px] rounded-lg px-4 text-[12.5px] font-semibold text-white"
              style={{ background: '#DC143C' }}>Use selected</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockPickerModal;
